<template>
  <div class="chat-menu">
    <Loading :isLoading="loading" v-if="loading"></Loading>
    <div class="sessions" v-else>
      <div @click="createSession" class="create-new-session">
        <font-awesome-icon size="lg" icon="plus" class="button-icon"></font-awesome-icon> New Session
      </div>

      <div class="session-group" v-for="group in groupedSessions" :key="group.key">
        <div v-if="group.sessions.length > 0">
          <div class="group-title"><strong>{{ group.key }}</strong></div>
          <div
            v-for="chatSession in group.sessions"
            :key="`chat-sessions-${chatSession.id}`"
            class="session-link"
            :class="{ active: isActive(chatSession), editing: editingSessionId === chatSession.id }"
            @click="onSessionClick(chatSession)"
          >
            <span
              v-if="editingSessionId !== chatSession.id"
              class="session-title"
            >{{ chatSession.title }}</span>
            <input
              v-else
              ref="editInput"
              v-model="editingTitle"
              class="session-title-input"
              @blur="saveTitle(chatSession)"
              @keydown.enter.prevent="saveTitle(chatSession)"
              @keydown.esc.prevent="cancelEdit"
              @click.stop
            />
            <button
              type="button"
              class="rename-session"
              @click.stop.prevent="startEditTitle(chatSession)"
              aria-label="Rename session"
            >
              <font-awesome-icon icon="pen" />
            </button>
            <button
              type="button"
              class="delete-session"
              @click.stop.prevent="onDeleteSession(chatSession)"
              aria-label="Delete session"
            >
              <font-awesome-icon icon="trash" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import Loading from "@/components/shared/Loading"
export default {
  name: "ChatMenu",
  components: {
    Loading
  },
  computed: {
    ...mapState({
      chatSessions: state => state.chat.sessions,
      loading: state => state.chat.loading.allSessions
    }),
    groupedSessions() {
      const groupedData = {
        Today: [],
        Yesterday: [],
        "Last 7 Days": [],
        "Last 30 days": [],
        Older: []
      }
      this.chatSessions.forEach((session) => {
        const d = moment(session.createdAt).startOf('day')
        const dayDiff = d.diff(moment(), 'days')
        let key = "Older"
        if (dayDiff === 0) key = "Today"
        else if (dayDiff === -1) key = "Yesterday"
        else if (dayDiff >= -7) key = "Last 7 Days"
        else if (dayDiff >= -30) key = "Last 30 days"

        groupedData[key].push(session)


        return true
      })


      groupedData.Today.sort((a, b) => { return new Date(b.createdAt) - new Date(a.createdAt) })
      groupedData.Yesterday.sort((a, b) => { return new Date(b.createdAt) - new Date(a.createdAt) })
      groupedData["Last 7 Days"].sort((a, b) => { return new Date(b.createdAt) - new Date(a.createdAt) })
      groupedData["Last 30 days"].sort((a, b) => { return new Date(b.createdAt) - new Date(a.createdAt) })


      const groupedArray = []
      groupedArray.push({ key: "Today", sessions: groupedData.Today || [] })
      groupedArray.push({ key: "Yesterday", sessions: groupedData.Yesterday || [] })
      groupedArray.push({ key: "Last 7 Days", sessions: groupedData["Last 7 Days"] || [] })
      groupedArray.push({ key: "Last 30 days", sessions: groupedData["Last 30 days"] || [] })
      return groupedArray
    }
  },
  data() {
    return {
      editingSessionId: null,
      editingTitle: ""
    }
  },
  mounted() {
    console.log("getting all sessions...")
    this.$store.dispatch("getAllSessions")
  },
  methods: {
    isActive(chatSession) {
      const current = this.$route.params.sessionId
      if (current == null || current === "") return false
      return String(this.$route.params.sessionId) === String(chatSession.id)
    },
    onSessionClick(chatSession) {
      if (this.editingSessionId === chatSession.id) return
      this.$router.push({ name: "ChatSession", params: { sessionId: chatSession.id } })
    },
    startEditTitle(chatSession) {
      this.editingSessionId = chatSession.id
      this.editingTitle = chatSession.title || ""
      this.$nextTick(() => {
        const input = this.$refs.editInput
        if (Array.isArray(input)) {
          if (input[0]) input[0].focus()
        } else if (input) {
          input.focus()
        }
      })
    },
    async saveTitle(chatSession) {
      if (this.editingSessionId !== chatSession.id) return
      const title = (this.editingTitle && this.editingTitle.trim()) || chatSession.title
      this.editingSessionId = null
      this.editingTitle = ""
      if (title !== chatSession.title) {
        await this.$store.dispatch("updateChatSessionTitle", { sessionId: chatSession.id, title: title.trim() })
      }
    },
    cancelEdit() {
      this.editingSessionId = null
      this.editingTitle = ""
    },
    async onDeleteSession(chatSession) {
      const wasActive = this.isActive(chatSession)
      try {
        await this.$store.dispatch("deleteChatSession", chatSession.id)
        if (wasActive) {
          this.$router.push({ name: "EmptyChatSession" })
        }
      } catch (e) {
        // Error already shown by store
      }
    },
    async createSession() {
      const chatSession = await this.$store.dispatch("createChatSession")
      this.$router.push({ name: "ChatSession", params: { sessionId: chatSession.id } })
    },
    selectChatSession(chatSession) {
      this.$router.push({ name: "ChatSession", params: { sessionId: chatSession.id } })
    },
    timeAgoInWords(d) {
      return moment(d).fromNow()
    }
  }
}

</script>

<style lang="scss" scoped>
.chat-menu {
  min-width: 250px;
  padding: 10px;
  flex: 0;
  height: 100%;
  overflow-y: scroll;

  color: var(--session-list-group-header);
  background-color: var(--session-list-background);

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .create-new-session {
    margin: 20px 40px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    svg {
      margin-right: 7px;
    }
  }

  .group-title {
    margin-bottom: 10px;
  }

  .session-link {
    margin-bottom: 5px;
    padding: 3px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    position: relative;

    .session-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .session-title-input {
      flex: 1;
      min-width: 0;
      padding: 2px 4px;
      border: 1px solid #999;
      border-radius: 4px;
      font-size: inherit;
      background: #fff;
    }

    .rename-session,
    .delete-session {
      flex-shrink: 0;
      opacity: 0;
      padding: 2px 6px;
      border: none;
      background: transparent;
      cursor: pointer;
      color: var(--session-list-link);
      border-radius: 4px;
      transition: opacity 0.15s ease;
    }

    .rename-session:hover {
      color: var(--session-list-link);
      background: rgba(0, 0, 0, 0.06);
    }

    .delete-session:hover {
      color: #c00;
      background: rgba(0, 0, 0, 0.06);
    }

    &:hover .rename-session,
    &:hover .delete-session {
      opacity: 0.4;
    }

    &.editing .rename-session,
    &.editing .delete-session {
      opacity: 0;
    }

    &:hover {
      background-color: #ccc;
      border-radius: 10px;
    }

    &.active {
      background-color: #dddddd;
      border-radius: 5px;
    }

    a {
      text-decoration: none;
    }
  }

  a {
    color: var(--session-list-link);
  }

  .time-ago {
    color: var(--session-list-time-ago);
    font-size: 0.8em;
  }

  .sessions {
    flex: 1;

    .session-group {
      margin-bottom: 20px;
    }
  }
}
</style>